import { useState } from 'react';
import {
  useAdminTherapists,
  useAdminTherapistDetail,
  useUpdateTherapistStatus,
  type AdminTherapist,
  type AdminTherapistsFilters,
} from '@theraply/api-client';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
    SUSPENDED: 'bg-red-100 text-red-800',
    PENDING_VERIFICATION: 'bg-yellow-100 text-yellow-800',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function VerificationBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    APPROVED: 'bg-green-100 text-green-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    REJECTED: 'bg-red-100 text-red-800',
    NEEDS_INFO: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function StarRating({ rating, count }: { rating: number; count: number }) {
  return (
    <div className="flex items-center">
      <svg className="h-4 w-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      <span className="ml-1 text-sm text-gray-600">{rating.toFixed(1)} ({count})</span>
    </div>
  );
}

type VerificationFilter = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'NEEDS_INFO';

export default function TherapistsPage() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [verificationFilter, setVerificationFilter] = useState<VerificationFilter>('ALL');
  const [page, setPage] = useState(1);
  const [selectedTherapistId, setSelectedTherapistId] = useState<string | null>(null);
  const limit = 20;

  const filters: AdminTherapistsFilters = { page, limit };
  if (search) filters.search = search;
  if (verificationFilter !== 'ALL') filters.verification_status = verificationFilter;

  const { data, isLoading, error, refetch } = useAdminTherapists(filters);
  const { data: therapistDetail, isLoading: detailLoading } = useAdminTherapistDetail(selectedTherapistId || '');
  const updateStatus = useUpdateTherapistStatus();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleStatusChange = (therapistId: string, status: string) => {
    if (!confirm(`Are you sure you want to change this therapist's status to ${status}?`)) return;
    updateStatus.mutate({ id: therapistId, status });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading therapists...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error</h3>
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Failed to load therapists'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const therapists = data?.therapists || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Therapist Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Review and manage therapist accounts. {total > 0 && `${total} total therapist${total !== 1 ? 's' : ''}.`}
          </p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mt-6">
        <div className="flex space-x-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email..."
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Verification Status Filter */}
      <div className="mt-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {(['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'NEEDS_INFO'] as VerificationFilter[]).map((status) => (
              <button
                key={status}
                onClick={() => { setVerificationFilter(status); setPage(1); }}
                className={`${
                  verificationFilter === status
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium`}
              >
                {status === 'ALL' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase().replace('_', ' ')}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Therapist Detail Modal */}
      {selectedTherapistId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Therapist Details</h3>
              <button
                onClick={() => setSelectedTherapistId(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {detailLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : therapistDetail ? (
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-gray-500">Name</div>
                  <div className="text-sm text-gray-900">{therapistDetail.firstName} {therapistDetail.lastName}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Email</div>
                  <div className="text-sm text-gray-900">{therapistDetail.email}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Phone</div>
                  <div className="text-sm text-gray-900">{therapistDetail.phone || 'Not provided'}</div>
                </div>
                {therapistDetail.bio && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Bio</div>
                    <div className="text-sm text-gray-900">{therapistDetail.bio}</div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500">Status</div>
                    <div className="mt-1"><StatusBadge status={therapistDetail.status} /></div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Verification</div>
                    <div className="mt-1"><VerificationBadge status={therapistDetail.verificationStatus} /></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500">Rating</div>
                    <div className="text-sm text-gray-900">{therapistDetail.ratingAvg.toFixed(1)} / 5</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Sessions</div>
                    <div className="text-sm text-gray-900">{therapistDetail.sessionCount}</div>
                  </div>
                </div>
                {therapistDetail.professionalInfo && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-1">Professional Info</div>
                    <div className="bg-gray-50 rounded-md p-3 space-y-1">
                      {therapistDetail.professionalInfo.licenseType && (
                        <div className="text-sm"><span className="text-gray-500">License Type:</span> {therapistDetail.professionalInfo.licenseType}</div>
                      )}
                      {therapistDetail.professionalInfo.licenseState && (
                        <div className="text-sm"><span className="text-gray-500">License State:</span> {therapistDetail.professionalInfo.licenseState}</div>
                      )}
                      {therapistDetail.professionalInfo.licenseNumber && (
                        <div className="text-sm"><span className="text-gray-500">License #:</span> {therapistDetail.professionalInfo.licenseNumber}</div>
                      )}
                      {therapistDetail.professionalInfo.npiNumber && (
                        <div className="text-sm"><span className="text-gray-500">NPI:</span> {therapistDetail.professionalInfo.npiNumber}</div>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-sm font-medium text-gray-500">Joined</div>
                  <div className="text-sm text-gray-900">{formatDate(therapistDetail.createdAt)}</div>
                </div>
                {therapistDetail.documents && therapistDetail.documents.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-1">Documents ({therapistDetail.documents.length})</div>
                    <div className="space-y-1">
                      {therapistDetail.documents.map((doc) => (
                        <div key={doc.id} className="text-sm bg-gray-50 rounded p-2 flex items-center justify-between">
                          <span>{doc.fileName}</span>
                          <span className="text-xs text-gray-500">{doc.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status actions */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="text-sm font-medium text-gray-500 mb-2">Change Status</div>
                  <div className="flex flex-wrap gap-2">
                    {therapistDetail.status !== 'ACTIVE' && (
                      <button
                        onClick={() => handleStatusChange(therapistDetail.id, 'ACTIVE')}
                        disabled={updateStatus.isPending}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:bg-gray-400"
                      >
                        Activate
                      </button>
                    )}
                    {therapistDetail.status !== 'SUSPENDED' && (
                      <button
                        onClick={() => handleStatusChange(therapistDetail.id, 'SUSPENDED')}
                        disabled={updateStatus.isPending}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:bg-gray-400"
                      >
                        Suspend
                      </button>
                    )}
                    {therapistDetail.status !== 'INACTIVE' && (
                      <button
                        onClick={() => handleStatusChange(therapistDetail.id, 'INACTIVE')}
                        disabled={updateStatus.isPending}
                        className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 disabled:bg-gray-400"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Therapists Table */}
      <div className="mt-6">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Name</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Verification</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Rating</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Joined</th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">View</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {therapists.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-sm text-gray-500">
                    {search || verificationFilter !== 'ALL' ? 'No therapists match your filters' : 'No therapists found'}
                  </td>
                </tr>
              ) : (
                therapists.map((therapist: AdminTherapist) => (
                  <tr
                    key={therapist.id}
                    onClick={() => setSelectedTherapistId(therapist.id)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                      {therapist.firstName} {therapist.lastName}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{therapist.email}</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <VerificationBadge status={therapist.verificationStatus} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <StarRating rating={therapist.ratingAvg} count={therapist.reviewCount} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm"><StatusBadge status={therapist.status} /></td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(therapist.createdAt)}</td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <button className="text-indigo-600 hover:text-indigo-900">View</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="text-sm text-gray-700">
              Page {page} of {totalPages} ({total} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
